import atFlag from 'flag-icons/flags/4x3/at.svg';
import beFlag from 'flag-icons/flags/4x3/be.svg';
import czFlag from 'flag-icons/flags/4x3/cz.svg';
import esFlag from 'flag-icons/flags/4x3/es.svg';
import frFlag from 'flag-icons/flags/4x3/fr.svg';
import gbFlag from 'flag-icons/flags/4x3/gb.svg';
import huFlag from 'flag-icons/flags/4x3/hu.svg';
import itFlag from 'flag-icons/flags/4x3/it.svg';
import luFlag from 'flag-icons/flags/4x3/lu.svg';
import nlFlag from 'flag-icons/flags/4x3/nl.svg';
import plFlag from 'flag-icons/flags/4x3/pl.svg';
import ptFlag from 'flag-icons/flags/4x3/pt.svg';
import skFlag from 'flag-icons/flags/4x3/sk.svg';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveLocale } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * Flags for the supported markets, served from the flag-icons package
 * (https://github.com/lipis/flag-icons) - emoji flags don't render on Windows.
 * Only the SVGs imported here end up in the bundle.
 */
const FLAGS: Record<string, string> = {
  PL: plFlag,
  IT: itFlag,
  GB: gbFlag,
  FR: frFlag,
  BE: beFlag,
  NL: nlFlag,
  LU: luFlag,
  ES: esFlag,
  PT: ptFlag,
  AT: atFlag,
  HU: huFlag,
  SK: skFlag,
  CZ: czFlag,
};

// Figma flag cell: 16px tall with a subtle rounding. The width follows the
// SVG's own 4:3 ratio (no fixed width, no object-cover) so no flag is cropped;
// only the text-chip fallback pins the 21px cell width.
const flagFrame = 'h-4 shrink-0 overflow-hidden rounded-[3px] border border-black';

export function CountryFlag({ code, className }: { code: string; className?: string }) {
  const { i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const title = useMemo(() => countryName(code, locale), [code, locale]);
  const src = FLAGS[code.toUpperCase()];

  if (!src) {
    return (
      <span
        title={title}
        className={cn(
          flagFrame,
          'inline-flex w-[21px] items-center justify-center bg-muted text-[7px] font-semibold text-muted-foreground',
          className,
        )}
      >
        {code.toUpperCase()}
      </span>
    );
  }
  return <img src={src} alt={title} title={title} className={cn(flagFrame, 'w-auto', className)} />;
}

type CountryFlagsProps = {
  codes: readonly string[];
  /** How many flags to show before collapsing the rest into a "+N" chip. */
  max?: number;
  className?: string;
};

export function CountryFlags({ codes, max = 8, className }: CountryFlagsProps) {
  const visible = codes.slice(0, max);
  const hidden = codes.length - visible.length;

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      {visible.map((code) => (
        <CountryFlag key={code} code={code} />
      ))}
      {hidden > 0 ? (
        <span
          className="text-xs font-medium text-muted-foreground"
          title={codes.slice(max).join(', ')}
        >
          +{hidden}
        </span>
      ) : null}
    </span>
  );
}

function countryName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}
